import { LogCategory } from '@soteria/logger/browser';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { App } from './App';
import { config, validateConfig } from './config';
import { logger } from './core/logging';
import { initializeAppInsights } from './core/logging/appInsightsIntegration';
import { initializeLogLevel } from './core/logging/logLevelManager';
import { initializeUrlLogLevelSupport } from './core/logging/urlLogLevelSupport';
import { history } from './router';

/**
 * Initialize logging system with proper sequence and error handling
 */
async function initializeLogging(): Promise<void> {
  try {
    // Step 1: Initialize log level management first
    initializeLogLevel();

    // Step 2: Initialize URL parameter support
    initializeUrlLogLevelSupport();

    // Step 3: Initialize App Insights with React plugin and React Router integration
    if (config.monitoring.applicationInsights) {
      await initializeAppInsights({
        connectionString: config.monitoring.applicationInsights,
        history,
      });
    }

    logger.debug('Logging system with React plugin initialized', {
      category: LogCategory.SYSTEM,
      environment: import.meta.env.MODE,
      reactRouterIntegration: true,
    });
  } catch (error) {
    console.error('Failed to initialize logging system:', error);
    // Don't throw - allow app to continue without full logging
  }
}

/**
 * Initialize and start the application
 */
async function startApplication(): Promise<void> {
  try {
    // Validate environment configuration before starting the app
    validateConfig();

    // Initialize logging system before React app
    await initializeLogging();

    // Initialize React application
    const rootElement = document.getElementById('root');

    if (!rootElement) {
      throw new Error('Root element not found');
    }

    ReactDOM.createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  } catch (error) {
    console.error('Application startup failed:', error);
    // You could show a user-friendly error page here instead of crashing
    throw error;
  }
}

// Start the application
startApplication();
